import { ScrollView } from "react-native";
import type { Href } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { TenantLink } from "@src/features/tenants";
import type { CursorPager, FleetOperatorNoteSummary } from "@visioni/a3s-monitoring/app";
import { EMPTY, formatPreciseInstant, formatText, hasText } from "../format";
import { CursorPagerBar, countOf } from "./CursorPagerBar";

interface OperatorNotesTableProps {
  readonly items: readonly FleetOperatorNoteSummary[];
  /** Dim the rows while they are the previous page's, held until the next arrives. */
  readonly isStale: boolean;
  readonly clientHref: (clientNo: string) => Href;
  readonly activationHref: (ticketNo: string) => Href;
  /** The zone the Time column is shown in — the one the list is filtered in. */
  readonly timeZone: string;
  readonly pager: CursorPager;
  readonly nextCursor: string | null;
  readonly isFetching: boolean;
}

const COLUMNS = [
  { label: "Time", style: "time" },
  { label: "Client", style: "client" },
  { label: "Activation", style: "ticket" },
  { label: "Type", style: "type" },
  { label: "Note", style: "note" },
  { label: "Operator", style: "operator" },
] as const;

/** Operator notes, one per row, with the client and activation each was written against linked. */
export function OperatorNotesTable({
  items,
  isStale,
  clientHref,
  activationHref,
  timeZone,
  pager,
  nextCursor,
  isFetching,
}: OperatorNotesTableProps) {
  return (
    <View flex={1}>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.card, isStale && styles.stale]}>
          <View style={[styles.row, styles.headerRow]}>
            {COLUMNS.map((column) => (
              <Text key={column.label} textStyle="xs/semibold" color="textMuted" style={styles[column.style]}>
                {column.label}
              </Text>
            ))}
          </View>
          {items.map((note) => (
            <OperatorNoteCells
              key={note.noteId}
              note={note}
              clientHref={clientHref}
              activationHref={activationHref}
              timeZone={timeZone}
            />
          ))}
          {items.length === 0 && (
            <View style={styles.row}>
              <Text textStyle="sm/regular" color="textMuted">
                {nextCursor === null ? "No more notes" : "None on this page — more may follow"}
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
      <CursorPagerBar
        pager={pager}
        nextCursor={nextCursor}
        isFetching={isFetching}
        summary={countOf(items.length, "note")}
      />
    </View>
  );
}

interface OperatorNoteCellsProps {
  readonly note: FleetOperatorNoteSummary;
  readonly clientHref: (clientNo: string) => Href;
  readonly activationHref: (ticketNo: string) => Href;
  readonly timeZone: string;
}

function OperatorNoteCells({ note, clientHref, activationHref, timeZone }: OperatorNoteCellsProps) {
  return (
    <View style={styles.row}>
      <Text textStyle="xs/regular" color="text" style={styles.time}>
        {formatPreciseInstant(note.noteDateTime, timeZone)}
      </Text>
      <View style={styles.client}>
        <CellLink href={clientHref(note.clientNo)} label={note.clientNo} />
      </View>
      <View style={styles.ticket}>
        {
          note.ticketNo !== undefined ?
            <CellLink href={activationHref(note.ticketNo)} label={note.ticketNo} />
            // Normal for an autodial call log, which Patriot writes against the client alone.
          : <Text textStyle="xs/regular" color="text">
              {EMPTY}
            </Text>

        }
      </View>
      <Text textStyle="xs/regular" color="text" style={styles.type} numberOfLines={2}>
        {formatText(note.noteType)}
      </Text>
      <View style={styles.note}>
        {hasText(note.description) && (
          <Text textStyle="sm/semibold" color="text" numberOfLines={1}>
            {note.description}
          </Text>
        )}
        <Text textStyle="xs/regular" color="text" numberOfLines={3} selectable>
          {formatText(note.notes)}
        </Text>
      </View>
      <Text textStyle="xs/regular" color="text" style={styles.operator} numberOfLines={1}>
        {formatText(note.operatorLogin)}
      </Text>
    </View>
  );
}

function CellLink({ href, label }: { readonly href: Href; readonly label: string }) {
  return (
    <TenantLink href={href} style={styles.link}>
      {({ hovered }) => (
        <Text textStyle="sm/semibold" color="text" style={hovered && styles.hoveredLink}>
          {label}
        </Text>
      )}
    </TenantLink>
  );
}

const styles = StyleSheet.create((theme) => ({
  scroll: { flex: 1 },
  scrollContent: { padding: theme.spacing.xl },
  card: {
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
    overflow: "hidden",
  },
  stale: { opacity: 0.5 },
  link: { cursor: "pointer" },
  hoveredLink: { textDecorationLine: "underline" },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.lg,
    borderTopWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  headerRow: { borderTopWidth: 0, backgroundColor: theme.colors.surfaceCardInner },
  time: { width: 200 },
  client: { width: 130 },
  ticket: { width: 100 },
  type: { width: 170 },
  note: { flex: 3, minWidth: 220, gap: theme.spacing.xs },
  operator: { flex: 1, minWidth: 100 },
}));
